// Truy vấn giao dịch (deal-service qua gateway). Phạm vi deals.view (own/team/all + người giới thiệu) do service lọc — FE
// không lọc theo quyền. Hook nhận `enabled`; điều kiện quyền / module do lớp trên tính (useCan, useViewer) rồi truyền vào.
import { keepPreviousData, useIsMutating, useQuery, type QueryClient } from '@tanstack/react-query';
import type { booking } from '@cathouse/contracts';
import { http, invalidateSoon, qk } from '@/shared/api';
import { syncServerTime } from '@/shared/lib';
import {
  DEAL_LIST_LIMIT,
  type DealAnnouncement,
  type DealDetail,
  type DealList,
  type DealListFilters,
  type DealSearchHit,
  type DealSubmissionView,
  type DealsOverview,
  type UnitChangeView,
} from './model';

export type SwapCandidate = booking.SwapCandidate;

/** Danh sách theo tab + số đếm từng tab (cùng phạm vi + dự án + từ khoá), sắp ở server. */
export const useDeals = (f: DealListFilters, enabled = true) =>
  useQuery({
    queryKey: qk.deals.list(f),
    queryFn: async ({ signal }) => {
      const r = await http.get<DealList>('/deals', { query: { ...f, page: 1, pageSize: DEAL_LIST_LIMIT }, signal });
      syncServerTime(r.serverTime);
      return r;
    },
    placeholderData: keepPreviousData,
    enabled,
  });

/** 4 KPI của tháng (giờ VN): giá trị giao dịch, chờ duyệt, đợt quá hạn / đến hạn 7 ngày, phí CĐT chưa thu. */
export const useDealsOverview = (month: string, enabled = true) =>
  useQuery({
    queryKey: qk.deals.overview(month),
    queryFn: ({ signal }) => http.get<DealsOverview>('/deals/overview', { query: { month }, signal }),
    placeholderData: keepPreviousData,
    enabled,
  });

/** Drawer: chi tiết + lịch thanh toán + diễn biến + nút theo `actions` (service tính theo trạng thái × quyền × saga). */
export const useDeal = (id: string | null | undefined) =>
  useQuery({
    queryKey: qk.deals.detail(id ?? ''),
    queryFn: async ({ signal }) => {
      const r = await http.get<DealDetail>(`/deals/${id}`, { signal });
      syncServerTime(r.serverTime);
      return r;
    },
    enabled: !!id,
  });

const SUBMISSION_DONE = new Set<DealSubmissionView['state']>(['submitted', 'failed', 'stuck']);
/** Lượt gửi phiếu (SG-1) chưa xong ⇒ hỏi lại mỗi 500 ms tới submitted / failed / stuck (FSD §4.4). */
export const useDealSubmission = (id: string | null | undefined, enabled = true) =>
  useQuery({
    queryKey: qk.deals.submission(id ?? ''),
    queryFn: ({ signal }) => http.get<DealSubmissionView>(`/deals/${id}/submission`, { signal }),
    enabled: !!id && enabled,
    refetchInterval: (q) => (q.state.data && SUBMISSION_DONE.has(q.state.data.state) ? false : 500),
    refetchOnWindowFocus: false,
    gcTime: 0,
  });

/** Tin báo chốt (văn bản server, không emoji) — gọi khi bấm. */
export const announcementQuery = (id: string) => ({
  queryKey: qk.deals.announcement(id),
  queryFn: () => http.get<DealAnnouncement>(`/deals/${id}/announcement`),
  staleTime: 0,
});

/** Căn để đổi (booking): cùng dự án, khác căn hiện tại, còn trống hoặc đang do người chốt giữ — gọi khi bấm "Đổi căn". */
export const unitChangeOptionsQuery = (id: string) => ({
  queryKey: qk.deals.unitChangeOptions(id),
  queryFn: () => http.get<SwapCandidate[]>(`/deals/${id}/unit-change-options`),
  staleTime: 0,
});

const SAGA_DONE = new Set<UnitChangeView['state']>(['completed', 'failed', 'stuck']);
export const unitChangeFinished = (v: UnitChangeView | undefined) => !!v && SAGA_DONE.has(v.state);
/** Đổi căn (SG-6, 202 { sagaId }) ⇒ hỏi lại mỗi 500 ms tới completed / failed / stuck. */
export const useUnitChange = (sagaId: string | null | undefined, enabled = true) =>
  useQuery({
    queryKey: qk.deals.unitChange(sagaId ?? ''),
    queryFn: ({ signal }) => http.get<UnitChangeView>(`/deals/unit-changes/${sagaId}`, { signal }),
    enabled: !!sagaId && enabled,
    refetchInterval: (q) => (unitChangeFinished(q.state.data) ? false : 500),
    refetchOnWindowFocus: false,
    gcTime: 0,
  });

/** Nguồn Tìm nhanh (Ctrl K): mã GD, số HĐ, mã căn — trong phạm vi. */
export const searchDeals = (q: string, signal?: AbortSignal) => http.get<DealSearchHit[]>('/deals/search', { query: { q, limit: 6 }, signal });

/**
 * Làm mới sau mọi thao tác giao dịch: danh sách / drawer / KPI ('deals', gồm giao dịch trong hồ sơ khách), bảng hàng, hồ sơ
 * khách, hộp việc; sau 1,5 s phần projection bất đồng bộ (hoa hồng, doanh số, Tổng quan, hộp việc).
 */
export function refreshDeals(qc: QueryClient, customerId?: string | null) {
  void qc.invalidateQueries({ queryKey: qk.deals.all });
  void qc.invalidateQueries({ queryKey: qk.booking.all });
  void qc.invalidateQueries({ queryKey: qk.inbox.all });
  if (customerId) void qc.invalidateQueries({ queryKey: qk.customers.detail(customerId) });
  invalidateSoon(qc, [qk.deals.all, qk.commission.all, qk.sales.all, qk.dashboard.all, qk.inbox.all, qk.booking.all]);
}

/** Khoá mutation của một thao tác trên giao dịch — mọi nút của drawer khoá khi có thao tác đang chạy (như `busy` prototype). */
export const dealActionKey = (dealId: string, action: string) => [...qk.deals.detail(dealId), action] as const;
/** Có thao tác nào đang chạy trên giao dịch này. */
export const useDealBusy = (dealId: string) => useIsMutating({ mutationKey: qk.deals.detail(dealId) }) > 0;
